import { Component, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { DateFormats, DateTimeHelper, TimeService } from '@vectronic/util';
import moment, { Moment } from 'moment';
import { FilterHeaderComponent } from '../filter-header/filter-header.component';
import { BaseFilterComponent } from '../filter/base-filter.component';

import 'moment-timezone';

@Component({
  selector: 'vectronic-utc-filter',
  templateUrl: './utc-filter.component.html',
  styleUrls: ['./utc-filter.component.scss'],
  providers: [provideMomentDateAdapter(new DateFormats().returnInternationalDateFormat())],
  imports: [
    FilterHeaderComponent,
    MatSelectModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatDatepickerModule,
    MatInputModule,
  ],
})
export class UtcFilterComponent extends BaseFilterComponent implements OnInit {
  readonly dateFormat = DateTimeHelper.DATE_FORMAT;
  timeService = inject(TimeService);

  constructor() {
    super();

    effect(() => {
      this.timeService.localTimeZone();
      this.refreshForTimezone();
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.ensureDateControls();
    this.syncDateControlsFromBackend();
    this.setupDateControlSubscriptions();
  }

  private toUtcRange(date: Moment, timezone: string) {
    const day = date.format('YYYY-MM-DD');

    return {
      from: moment.tz(day, timezone).startOf('day').toISOString(),
      to: moment.tz(day, timezone).endOf('day').toISOString(),
    };
  }

  onRecentValChange() {
    this.filtersForm().get(this.controlName()).setValue('recent');
  }

  onRadioChange(event: MatRadioChange) {
    if (event?.value === 'recent') {
      this.filtersForm().get(this.utcFrom()).setValue('');
      this.filtersForm().get(this.utcTo()).setValue('');
      this.filtersForm().get(this.lmtFrom()).setValue('');
      this.filtersForm().get(this.lmtTo()).setValue('');
      this.filtersForm().get(this.utcFromDateControlName()).setValue(null, { emitEvent: false });
      this.filtersForm().get(this.utcToDateControlName()).setValue(null, { emitEvent: false });
      this.filtersForm().get(this.lmtFromDateControlName()).setValue(null, { emitEvent: false });
      this.filtersForm().get(this.lmtToDateControlName()).setValue(null, { emitEvent: false });
      return;
    }

    if (event?.value === 'time-span') {
      this.syncUtcModeFromDates();
      return;
    }

    if (event?.value === 'time-span-lmt') {
      this.syncLmtModeFromDates();
    }
  }

  private syncUtcModeFromDates() {
    const form = this.filtersForm();
    if (!form) return;

    let utcFromDate = this.normalizeDateValue(form.get(this.utcFromDateControlName())?.value);
    let utcToDate = this.normalizeDateValue(form.get(this.utcToDateControlName())?.value);

    if (!utcFromDate) {
      const lmtFromDate = this.normalizeDateValue(form.get(this.lmtFromDateControlName())?.value);
      if (lmtFromDate) {
        utcFromDate = lmtFromDate;
        form.get(this.utcFromDateControlName()).setValue(utcFromDate, { emitEvent: false });
      }
    }

    if (!utcToDate) {
      const lmtToDate = this.normalizeDateValue(form.get(this.lmtToDateControlName())?.value);
      if (lmtToDate) {
        utcToDate = lmtToDate;
        form.get(this.utcToDateControlName()).setValue(utcToDate, { emitEvent: false });
      }
    }

    if (utcFromDate) {
      form.get(this.utcFrom()).setValue(this.utcDateToIsoStart(utcFromDate), {
        emitEvent: false,
      });
    } else {
      form.get(this.utcFrom()).setValue('', { emitEvent: false });
    }

    if (utcToDate) {
      form.get(this.utcTo()).setValue(this.utcDateToIsoEnd(utcToDate), { emitEvent: false });
    } else {
      form.get(this.utcTo()).setValue('', { emitEvent: false });
    }
  }

  private syncLmtModeFromDates() {
    const form = this.filtersForm();
    if (!form) return;

    let lmtFromDate = this.normalizeDateValue(form.get(this.lmtFromDateControlName())?.value);
    let lmtToDate = this.normalizeDateValue(form.get(this.lmtToDateControlName())?.value);

    if (!lmtFromDate) {
      const utcFromDate = this.normalizeDateValue(form.get(this.utcFromDateControlName())?.value);
      if (utcFromDate) {
        lmtFromDate = utcFromDate;
        form.get(this.lmtFromDateControlName()).setValue(lmtFromDate, { emitEvent: false });
      }
    }

    if (!lmtToDate) {
      const utcToDate = this.normalizeDateValue(form.get(this.utcToDateControlName())?.value);
      if (utcToDate) {
        lmtToDate = utcToDate;
        form.get(this.lmtToDateControlName()).setValue(lmtToDate, { emitEvent: false });
      }
    }

    form.get(this.lmtFrom()).setValue(lmtFromDate ?? '', { emitEvent: false });
    form.get(this.lmtTo()).setValue(lmtToDate ?? '', { emitEvent: false });

    const timezone = this.timeService.localTimeZone();

    if (lmtFromDate) {
      const { from } = this.toUtcRange(lmtFromDate, timezone);
      form.get(this.utcFrom()).setValue(from, { emitEvent: false });
    } else {
      form.get(this.utcFrom()).setValue('', { emitEvent: false });
    }

    if (lmtToDate) {
      const { to } = this.toUtcRange(lmtToDate, timezone);
      form.get(this.utcTo()).setValue(to, { emitEvent: false });
    } else {
      form.get(this.utcTo()).setValue('', { emitEvent: false });
    }
  }

  refreshForTimezone() {
    const form = this.filtersForm();
    if (!form) return;
    if (form.get(this.controlName())?.value !== 'time-span-lmt') return;

    const timezone = this.timeService.localTimeZone();
    const lmtFromDate = this.normalizeDateValue(form.get(this.lmtFromDateControlName())?.value);
    const lmtToDate = this.normalizeDateValue(form.get(this.lmtToDateControlName())?.value);

    if (lmtFromDate) {
      const { from } = this.toUtcRange(lmtFromDate, timezone);
      form.get(this.utcFrom()).setValue(from, { emitEvent: false });
    } else {
      form.get(this.utcFrom()).setValue('', { emitEvent: false });
    }

    if (lmtToDate) {
      const { to } = this.toUtcRange(lmtToDate, timezone);
      form.get(this.utcTo()).setValue(to, { emitEvent: false });
    } else {
      form.get(this.utcTo()).setValue('', { emitEvent: false });
    }
  }

  private ensureDateControls() {
    const form = this.filtersForm();
    if (!form) return;

    const controls = [
      this.utcFromDateControlName(),
      this.utcToDateControlName(),
      this.lmtFromDateControlName(),
      this.lmtToDateControlName(),
    ];

    for (const controlName of controls) {
      if (!form.get(controlName)) {
        form.addControl(controlName, new FormControl<Moment | null>(null));
      }
    }
  }

  private setupDateControlSubscriptions() {
    const form = this.filtersForm();
    if (!form) return;

    const utcFromDateControl = form.get(this.utcFromDateControlName());
    const utcToDateControl = form.get(this.utcToDateControlName());
    const lmtFromDateControl = form.get(this.lmtFromDateControlName());
    const lmtToDateControl = form.get(this.lmtToDateControlName());

    utcFromDateControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleUtcDateChange('from', value));
    utcToDateControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleUtcDateChange('to', value));
    lmtFromDateControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleLmtDateChange('from', value));
    lmtToDateControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.handleLmtDateChange('to', value));
  }

  private syncDateControlsFromBackend() {
    const form = this.filtersForm();
    if (!form) return;

    const mode = form.get(this.controlName())?.value;
    const utcFromIso = form.get(this.utcFrom())?.value;
    const utcToIso = form.get(this.utcTo())?.value;
    const timezone = this.timeService.localTimeZone();

    this.syncUtcDateControlFromBackend(form, 'from', utcFromIso);
    this.syncUtcDateControlFromBackend(form, 'to', utcToIso);

    this.syncLmtDateControlFromBackend(form, 'from', mode, utcFromIso, timezone);
    this.syncLmtDateControlFromBackend(form, 'to', mode, utcToIso, timezone);
  }

  private syncUtcDateControlFromBackend(form: FormGroup, which: 'from' | 'to', utcIso: string) {
    const dateControlName =
      which === 'from' ? this.utcFromDateControlName() : this.utcToDateControlName();
    const utcDateValue = this.normalizeDateValue(form.get(dateControlName)?.value);
    if (utcDateValue) {
      this.setDateControl(dateControlName, utcDateValue);
    } else if (utcIso) {
      this.setDateControl(dateControlName, this.isoToUtcDateOnly(utcIso));
    }
  }

  private syncLmtDateControlFromBackend(
    form: FormGroup,
    which: 'from' | 'to',
    mode: string,
    utcIso: string,
    timezone: string,
  ) {
    const dateControlName =
      which === 'from' ? this.lmtFromDateControlName() : this.lmtToDateControlName();
    const lmtControlName = which === 'from' ? this.lmtFrom() : this.lmtTo();

    const lmtDateValue = this.normalizeDateValue(form.get(dateControlName)?.value);
    if (lmtDateValue) {
      this.setDateControl(dateControlName, lmtDateValue);
    } else {
      const lmtRaw = form.get(lmtControlName)?.value;
      if (lmtRaw) {
        this.setDateControl(dateControlName, this.normalizeDateValue(lmtRaw));
      } else if (mode === 'time-span-lmt' && utcIso) {
        this.setDateControl(dateControlName, this.isoToTimezoneDateOnly(utcIso, timezone));
      }
    }
  }

  private handleUtcDateChange(which: 'from' | 'to', rawValue: unknown) {
    const form = this.filtersForm();
    if (!form) return;

    const normalized = this.normalizeDateValue(rawValue);
    const dateControlName =
      which === 'from' ? this.utcFromDateControlName() : this.utcToDateControlName();
    form.get(dateControlName).setValue(normalized, { emitEvent: false });
    const controlName = which === 'from' ? this.utcFrom() : this.utcTo();

    if (!normalized) {
      form.get(controlName).setValue('', { emitEvent: false });
      return;
    }

    const utcIso =
      which === 'from' ? this.utcDateToIsoStart(normalized) : this.utcDateToIsoEnd(normalized);
    form.get(controlName).setValue(utcIso, { emitEvent: false });
    form.get(this.controlName()).setValue('time-span', { emitEvent: false });
  }

  private handleLmtDateChange(which: 'from' | 'to', rawValue: unknown) {
    const form = this.filtersForm();
    if (!form) return;

    const normalized = this.normalizeDateValue(rawValue);
    const dateControlName =
      which === 'from' ? this.lmtFromDateControlName() : this.lmtToDateControlName();
    form.get(dateControlName).setValue(normalized, { emitEvent: false });
    const lmtControlName = which === 'from' ? this.lmtFrom() : this.lmtTo();
    const utcControlName = which === 'from' ? this.utcFrom() : this.utcTo();

    form.get(lmtControlName).setValue(normalized ?? '', { emitEvent: false });

    if (!normalized) {
      form.get(utcControlName).setValue('', { emitEvent: false });
      return;
    }

    const timezone = this.timeService.localTimeZone();
    const { from, to } = this.toUtcRange(normalized, timezone);
    const utcValue = which === 'from' ? from : to;
    form.get(utcControlName).setValue(utcValue, { emitEvent: false });
    form.get(this.controlName()).setValue('time-span-lmt', { emitEvent: false });
  }

  utcFromDateControlName() {
    return `${this.utcFrom()}Date`;
  }

  utcToDateControlName() {
    return `${this.utcTo()}Date`;
  }

  lmtFromDateControlName() {
    return `${this.lmtFrom()}Date`;
  }

  lmtToDateControlName() {
    return `${this.lmtTo()}Date`;
  }

  private normalizeDateValue(value: unknown): Moment | null {
    if (!value) return null;
    const parsed = moment.isMoment(value) ? value : moment(value as string);
    if (!parsed.isValid()) return null;
    return moment({
      year: parsed.year(),
      month: parsed.month(),
      date: parsed.date(),
    });
  }

  private utcDateToIsoStart(date: Moment) {
    return moment
      .utc()
      .year(date.year())
      .month(date.month())
      .date(date.date())
      .startOf('day')
      .toISOString();
  }

  private utcDateToIsoEnd(date: Moment) {
    return moment
      .utc()
      .year(date.year())
      .month(date.month())
      .date(date.date())
      .endOf('day')
      .toISOString();
  }

  private isoToUtcDateOnly(iso: string) {
    if (!iso) return null;
    const parsed = moment.utc(iso);
    if (!parsed.isValid()) return null;
    return moment({
      year: parsed.year(),
      month: parsed.month(),
      date: parsed.date(),
    });
  }

  private isoToTimezoneDateOnly(iso: string, timezone: string) {
    if (!iso) return null;
    const parsed = moment.tz(iso, timezone);
    if (!parsed.isValid()) return null;
    return moment({
      year: parsed.year(),
      month: parsed.month(),
      date: parsed.date(),
    });
  }

  private setDateControl(controlName: string, value: Moment | null) {
    const form = this.filtersForm();
    if (!form) return;
    form.get(controlName).setValue(value, { emitEvent: false });
  }
}
